// src/pages/OnboardingMenteePage.js
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import Logo from '../components/Logo';
import PrivacyAccordion from '../components/PrivacyAccordion';
import PasswordField from '../components/forms/PasswordField';

import { authApi, mentoringApi } from '../axiosInstance';

/* ===========================
   Utils
   =========================== */

// Fehler/Objekte sicher in Text verwandeln (für Rendering & setError)
function toText(val) {
  if (val == null) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'object') {
    // Häufige Felder in API-Fehlern
    return val.error || val.message || JSON.stringify(val);
  }
  try {
    return String(val);
  } catch {
    return '';
  }
}

// SHA-256 Hash für Consent-Texte (Protokollierung)
async function hashConsentText(text) {
  const enc = new TextEncoder().encode(text);
  const buf = await window.crypto.subtle.digest('SHA-256', enc);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

// Cookie-Fallback
function readCookie(name) {
  const hit = document.cookie
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${name}=`));
  return hit ? decodeURIComponent(hit.split('=')[1]) : '';
}

/* ===========================
   Component
   =========================== */

export default function OnboardingMenteePage({ csrfToken }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  // Fehler immer als String halten
  const [error, setError] = useState('');

  // ✅ Skip Password, wenn User aus Login kommt (Profil unvollständig, aber Passwort existiert)
  const [skipPassword, setSkipPassword] = useState(() => {
    try {
      return sessionStorage.getItem('ONBOARDING_SKIP_PASSWORD') === '1';
    } catch {
      return false;
    }
  });

  // Namen
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  // Passwort + Bestätigung
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Live-Policy vom PasswordField
  const [passwordPolicy, setPasswordPolicy] = useState(null);
  // undefined = PasswordField hat noch nicht gemeldet → Fallback verwenden
  const [passwordValid, setPasswordValid] = useState(undefined);
  const [passwordIssues, setPasswordIssues] = useState([]);

  // Consents
  const [contractConsent, setContractConsent] = useState(false);
  const [analysisConsent, setAnalysisConsent] = useState(false);

  // UI-State
  const [submitting, setSubmitting] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  // ✅ Skip-Flag aus sessionStorage lesen
  useEffect(() => {
    try {
      setSkipPassword(sessionStorage.getItem('ONBOARDING_SKIP_PASSWORD') === '1');
    } catch {
      setSkipPassword(false);
    }
  }, []);

  const clearSkipPasswordFlag = () => {
    try {
      sessionStorage.removeItem('ONBOARDING_SKIP_PASSWORD');
    } catch {}
  };

  // HashRouter-kompatibel "?lang=" aus location.hash lesen
  const getLangFromHash = () => {
    const hash = typeof window !== 'undefined' ? window.location.hash : '';
    const qs = hash.split('?')[1] || '';
    return new URLSearchParams(qs).get('lang');
  };

  /* ===========================
     ✅ NEU: Wenn Onboarding schon abgeschlossen ist → sofort raus (keine 400er mehr)
     =========================== */
  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const r = await authApi.get('/whoami', { withCredentials: true });
        const u = r?.data?.user || null;

        const onboardingDone =
          !!(u?.onboarding_complete ?? u?.onboardingComplete);

        const firstLogin =
          !!(u?.is_first_login ?? u?.first_login ?? u?.firstLogin);

        if (mounted && onboardingDone && !firstLogin) {
          // Flag aufräumen (sonst "skipPassword" bleibt in der Session hängen)
          clearSkipPasswordFlag();

          // Sprache robust bestimmen (HashRouter + Query)
          const urlLang = searchParams.get('lang') || getLangFromHash();
          const norm = (v) => (v && typeof v === 'string' ? v.toLowerCase().slice(0, 2) : null);
          const lang = norm(urlLang) || norm(i18n.language) || 'de';

          navigate(`/mentee-profile?lang=${lang}`, { replace: true });
        }
      } catch {
        // bewusst ignorieren: wenn whoami nicht geht, Verhalten wie bisher
      }
    })();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ===========================
     Sprache / Header
     =========================== */

  // Sprache aus URL / localStorage initialisieren
  useEffect(() => {
    let urlLang = searchParams.get('lang') || getLangFromHash();
    const saved = localStorage.getItem('language') || localStorage.getItem('preferred_lang');

    const norm = (v) => (v && typeof v === 'string' ? v.toLowerCase().slice(0, 2) : null);
    const desired = norm(urlLang) || norm(saved);

    if (desired === 'de' || desired === 'en') {
      if (i18n.language !== desired) i18n.changeLanguage(desired);
      try {
        localStorage.setItem('language', desired);
        localStorage.setItem('preferred_lang', desired);
      } catch {}
    }
  }, [searchParams, i18n]);

  // bei Sprachwechsel Accept-Language in beiden Axios-Instanzen setzen
  useEffect(() => {
    authApi.defaults.headers.common['Accept-Language'] = i18n.language;
    mentoringApi.defaults.headers.common['Accept-Language'] = i18n.language;
    try {
      localStorage.setItem('language', i18n.language);
      localStorage.setItem('preferred_lang', i18n.language);
    } catch {}
  }, [i18n.language]);

  /* ===========================
     Policy laden (mit Fallback)
     =========================== */
  useEffect(() => {
    if (skipPassword) {
      setPasswordPolicy(null);
      return;
    }

    let mounted = true;
    (async () => {
      try {
        const r = await authApi.get('/auth/password/policy', { withCredentials: true });
        if (mounted) setPasswordPolicy(r.data || null);
      } catch {
        // ältere Route (Fallback)
        try {
          const r2 = await authApi.get('/auth/password-policy', { withCredentials: true });
          if (mounted) setPasswordPolicy(r2.data || null);
        } catch {
          if (mounted) setPasswordPolicy(null); // PasswordField hat Defaults
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, [skipPassword]);

  /* ===========================
     Consent-Texte für Protokoll
     =========================== */
  const contractConsentText = t('onboardingMentee.contractConsentLong');
  const analysisConsentText = t('onboardingMentee.analysisConsentLong');
  const consentVersion = 'v1.0';

  const openPrivacyModal = (e) => {
    e.preventDefault();
    setShowPrivacyModal(true);
  };
  const closePrivacyModal = () => setShowPrivacyModal(false);

  const passwordsMatch = skipPassword
    ? true
    : newPassword.length > 0 && confirmPassword.length > 0 && newPassword === confirmPassword;

  /* ===========================
     CSRF laden (Route + Fallback)
     =========================== */
  async function getCsrf() {
    try {
      const r = await authApi.get('/csrf-token', { withCredentials: true });
      return r?.data?.csrfToken || '';
    } catch {
      return (
        csrfToken ||
        readCookie('_csrf') ||
        readCookie('XSRF-TOKEN') ||
        readCookie('CSRF-TOKEN') ||
        readCookie('csrfToken') ||
        ''
      );
    }
  }

  /* ===========================
     Submit
     =========================== */
  async function handleFinishOnboarding(e) {
    e.preventDefault();
    setError('');

    // Robuste Validierung: echtes OK vom PasswordField ODER Fallback (>= 12 Zeichen)
    const passValidEffective = skipPassword
      ? true
      : passwordValid === true || (passwordValid === undefined && newPassword.length >= 12);

    if (!passValidEffective) {
      setError(toText(t('onboardingMentee.passwordInvalid', 'Bitte ein sicheres Passwort wählen.')));
      return;
    }
    if (!passwordsMatch) {
      setError(toText(t('onboardingMentee.passwordsNoMatch')));
      return;
    }
    if (!contractConsent || !analysisConsent) {
      setError(toText(t('onboardingMentee.consentRequired')));
      return;
    }

    setSubmitting(true);
    try {
      const csrf = await getCsrf();

      // 1) Profil speichern (Mentoring-Server 4006)
      try {
        await mentoringApi.patch(
          '/mentoring/onboarding-mentee/profile',
          { firstName, lastName },
          {
            withCredentials: true,
            headers: {
              'X-CSRF-Token': csrf,
              'Accept-Language': i18n.language,
              'X-Requested-With': 'XMLHttpRequest',
            },
          }
        );
      } catch (e) {
        const msg = e?.response?.data;

        // Wenn Onboarding schon erledigt → NICHT blocken, einfach weiter
        if (
          e?.response?.status === 400 &&
          (msg === 'Onboarding schon abgeschlossen' ||
            msg?.error === 'Onboarding schon abgeschlossen')
        ) {
          // bewusst kein return / keine Navigation:
          // der restliche Onboarding-Flow soll weiterlaufen
        } else {
          throw e;
        }
      }

      // 2) Passwort setzen (Policy wird serverseitig erneut geprüft) – nur wenn nicht skip
      if (!skipPassword) {
        await authApi.post(
          '/onboarding-mentee/password',
          { newPassword },
          {
            withCredentials: true,
            headers: {
              'X-CSRF-Token': csrf,
              'Accept-Language': i18n.language,
              'X-Requested-With': 'XMLHttpRequest',
            },
          }
        );
      }

      // 3) Zwei Consents speichern (Mentoring-API 3006)
      const [contractHash, analysisHash] = await Promise.all([
        hashConsentText(contractConsentText),
        hashConsentText(analysisConsentText),
      ]);

      await mentoringApi.post(
        '/mentoring/consent',
        {
          consent_type: 'contract_participation',
          consent_status: 'granted',
          consent_version: consentVersion,
          consent_text_hash: contractHash,
          language: i18n.language,
        },
        { withCredentials: true, headers: { 'X-CSRF-Token': csrf, 'X-Requested-With': 'XMLHttpRequest' } }
      );

      await mentoringApi.post(
        '/mentoring/consent',
        {
          consent_type: 'mentor_match_analysis',
          consent_status: 'granted',
          consent_version: consentVersion,
          consent_text_hash: analysisHash,
          language: i18n.language,
        },
        { withCredentials: true, headers: { 'X-CSRF-Token': csrf, 'X-Requested-With': 'XMLHttpRequest' } }
      );

      // 4) Onboarding als abgeschlossen markieren
      try {
        await mentoringApi.post('/mentoring/onboarding/complete');
      } catch (err) {
        console.error('onboarding/complete failed:', err);
      }

      // 5) Weiter
      clearSkipPasswordFlag();
      navigate(`/mentee-profile?lang=${i18n.language}`);
    } catch (err) {
      // Fehler robust anzeigen (immer String)
      const payload = err?.response?.data;
      setError(
        typeof payload === 'string'
          ? payload
          : payload?.error || payload?.message || toText(t('onboardingMentee.loadingError'))
      );
    } finally {
      setSubmitting(false);
    }
  }

  /* ===========================
     Render
     =========================== */

  // Effektive Schaltflächen-Enable-Logik (mit Fallback, falls PasswordField noch nicht gemeldet hat)
  const passValidEffective = skipPassword
    ? true
    : passwordValid === true || (passwordValid === undefined && newPassword.length >= 12);

  const canSubmit =
    contractConsent && analysisConsent && passwordsMatch && !submitting && passValidEffective;

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-6 text-white">
        <p role="alert" className="text-red-500 text-lg">
          {toText(error)}
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center p-6 text-white">
      {/* Logo */}
      <div className="mb-10">
        <Logo variant="wordmark" size="xxxl" />
      </div>

      {/* Titel */}
      <h2 className="text-3xl md:text-4xl font-bold mb-4">
        {t('onboardingMentee.heading')}
      </h2>

      {/* Intro (i18n) */}
      <div className="bg-gray-800 p-4 rounded-2xl mb-8 w-full max-w-2xl text-white text-center">
        {t('onboardingMentee.intro')}
      </div>

      {/* Formular */}
      <form
        onSubmit={handleFinishOnboarding}
        className="bg-gray-800 p-8 rounded-2xl shadow-md w-full max-w-md"
      >
        {/* Vorname */}
        <div className="mb-6">
          <label htmlFor="firstName" className="block mb-2 font-semibold">
            {t('onboardingMentee.firstName')}
          </label>
          <input
            id="firstName"
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
            className="w-full p-2 bg-gray-700 rounded text-white"
            autoComplete="given-name"
          />
        </div>

        {/* Nachname */}
        <div className="mb-6">
          <label htmlFor="lastName" className="block mb-2 font-semibold">
            {t('onboardingMentee.lastName')}
          </label>
          <input
            id="lastName"
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
            className="w-full p-2 bg-gray-700 rounded text-white"
            autoComplete="family-name"
          />
        </div>

        {/* Passwort-Bereich nur wenn NICHT skip */}
        {!skipPassword && (
          <>
            {/* Passwort mit Live-Check */}
            <div className="mb-6">
              <label htmlFor="newPassword" className="block mb-2 font-semibold">
                {t('onboardingMentee.password')}
              </label>
              <PasswordField
                value={newPassword}
                onChange={setNewPassword}
                policy={passwordPolicy}
                onValidityChange={(ok, issues) => {
                  // ok kann true/false sein – wenn das Feld noch nicht entschieden hat, lassen wir undefined stehen
                  setPasswordValid(ok === true ? true : ok === false ? false : undefined);
                  setPasswordIssues(Array.isArray(issues) ? issues : []);
                }}
              />
              {passwordIssues.length > 0 && (
                <ul className="mt-2 text-xs text-red-400 list-disc ml-5">
                  {passwordIssues.map((it, idx) => (
                    <li key={idx}>{it}</li>
                  ))}
                </ul>
              )}
            </div>

            {/* Passwort wiederholen */}
            <div className="mb-6">
              <label htmlFor="confirmPassword" className="block mb-2 font-semibold">
                {t('onboardingMentee.confirmPassword')}
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="w-full p-2 bg-gray-700 rounded text-white"
                autoComplete="new-password"
              />
              {confirmPassword.length > 0 && !passwordsMatch && (
                <div className="text-xs text-red-400 mt-1">
                  {t('onboardingMentee.passwordsNoMatch')}
                </div>
              )}
            </div>
          </>
        )}

        {/* Consent 1 (mit Trans + Popup-Link) */}
        <div className="mb-4">
          <label
            htmlFor="contractConsent"
            className="flex items-start space-x-3 cursor-pointer select-none"
          >
            <input
              id="contractConsent"
              type="checkbox"
              checked={contractConsent}
              onChange={(e) => setContractConsent(e.target.checked)}
              className="mr-2 mt-1"
              required
            />
            <span>
              <Trans
                i18nKey="onboardingMentee.contractConsent"
                components={[
                  // eslint-disable-next-line
                  <button
                    onClick={(ev) => {
                      ev.preventDefault();
                      openPrivacyModal(ev);
                    }}
                    type="button"
                    className="underline text-mentixxgreen hover:text-green-300 focus:outline-none"
                    tabIndex={0}
                  />,
                ]}
              />
            </span>
          </label>
        </div>

        {/* Consent 2 */}
        <div className="mb-4">
          <label
            htmlFor="analysisConsent"
            className="flex items-start space-x-3 cursor-pointer select-none"
          >
            <input
              id="analysisConsent"
              type="checkbox"
              checked={analysisConsent}
              onChange={(e) => setAnalysisConsent(e.target.checked)}
              className="mr-2 mt-1"
              required
            />
            <span>{t('onboardingMentee.analysisConsent')}</span>
          </label>
          <div className="ml-6 mt-1 text-xs text-gray-300">
            {t('onboardingMentee.analysisConsentHint')}
          </div>
        </div>

        <button
          type="submit"
          className={`w-full bg-mentixxgreen hover:bg-green-400 text-black font-bold py-2 rounded-2xl transition mt-2 ${
            !canSubmit ? 'opacity-60 cursor-not-allowed' : ''
          }`}
          disabled={!canSubmit}
        >
          {submitting ? t('onboardingMentee.submitting') : t('onboardingMentee.finish')}
        </button>
      </form>

      {/* Datenschutz-Popup */}
      {showPrivacyModal && (
        <PrivacyAccordion
          userRole="mentees"
          language={i18n.language}
          onClose={closePrivacyModal}
        />
      )}
    </div>
  );
}
